import { format, formatDistanceToNow } from 'date-fns';
import { ChevronDown, MessageSquareText } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useRemoveRequest, useUpdateRequest } from '@/api/requests/queries';
import type { Request } from '@/api/requests/types';
import { useGetSubscription } from '@/api/subscription/queries';
import AndroidIcon from '@/assets/platform-icons/android-icon-small.svg?react';
import IosIcon from '@/assets/platform-icons/apple-icon.svg?react';
import { ConfirmDeleteAlert } from '@/components/confirm-delete-alert';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useMediaQuery } from '@/hooks';
import { PRIORITIES, STATUSES, type StatusType } from '../-utils/config';
import { ChangePriorityDropdown } from './change-priority-dropdown';
import { ChangeStatusDropdown } from './change-status-dropdown';
import { RequestVoteCount } from './request-vote-count';

type Props = {
  data: Request[];
  onItemClick: (id: string) => void;
};

type QuickActionsType = {
  tooltip: string;
};

const QUICK_ACTIONS_BY_STATUS: Record<string, Array<StatusType & QuickActionsType> | undefined> = {
  pending: [
    { ...STATUSES.inReview, tooltip: 'Mark as in review' },
    { ...STATUSES.approved, tooltip: 'Approve' },
    { ...STATUSES.rejected, tooltip: 'Reject' },
  ],
  approved: [{ ...STATUSES.inProgress, tooltip: 'Mark as in progress' }],
  'in-progress': [{ ...STATUSES.done, tooltip: 'Mark as completed' }],
  'in-review': [
    { ...STATUSES.approved, tooltip: 'Approve' },
    { ...STATUSES.rejected, tooltip: 'Reject' },
  ],
};

export const RequestsList = ({ data, onItemClick }: Props) => {
  const [requestToRemove, setRequestToRemove] = useState<Request | null>(null);
  const updateMutation = useUpdateRequest();
  const removeMutation = useRemoveRequest();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const { data: subscription } = useGetSubscription();

  const handleDeleteAction = (confirmed: boolean) => {
    if (!confirmed) {
      return setRequestToRemove(null);
    }

    if (!requestToRemove) {
      return;
    }

    setRequestToRemove(null);
    return removeMutation.mutateAsync(
      { requestId: requestToRemove.id, projectId: requestToRemove.projectId },
      { onSuccess: () => toast.success('Request removed') },
    );
  };

  const handleChangeStatus = (request: Request, status: string) => {
    return updateMutation.mutateAsync(
      {
        requestId: request.id,
        projectId: request.projectId,
        data: {
          name: request.name,
          description: request.description,
          status,
          note: request.note,
          publicNote: request.publicNote,
          priority: request.priority,
        },
      },
      { onSuccess: () => toast.success('Request status updated') },
    );
  };

  const handleChangePriority = (request: Request, priority: string) => {
    return updateMutation.mutateAsync(
      {
        requestId: request.id,
        projectId: request.projectId,
        data: {
          name: request.name,
          description: request.description,
          status: request.status,
          note: request.note,
          publicNote: request.publicNote,
          priority,
        },
      },
      { onSuccess: () => toast.success('Request priority updated') },
    );
  };

  return (
    <div className="divide-y divide-dotted">
      {data?.map((request) => {
        const status = Object.values(STATUSES).find((status) => status.status === request.status);
        const priority = Object.values(PRIORITIES).find(
          (priority) => priority.status === request.priority,
        );
        const quickActions = QUICK_ACTIONS_BY_STATUS[request.status];

        if (!isDesktop) {
          return (
            <div
              key={request.id}
              className="flex min-h-[70px] cursor-pointer flex-col gap-1 py-3 transition-colors hover:bg-slate-100/30 dark:hover:bg-slate-900/40"
              onClick={() => {
                onItemClick(request.id);
              }}
            >
              <div className="flex flex-1 items-center gap-3">
                <RequestVoteCount count={request.voteCount} />
                <div className="flex-1 flex-col">
                  <div className="line-clamp-2 font-semibold">{request.name}</div>
                  <div className="text-muted-foreground line-clamp-2 text-sm">
                    {request.description}
                  </div>
                </div>
              </div>
              <div className="flex flex-1 items-start gap-1.5">
                <div className="text-muted-foreground/60 mt-1 mr-auto flex items-center gap-1.5 space-x-0.5 text-sm">
                  <span className="mr-1 flex w-[36px] items-center">
                    {!subscription?.isFree && request.commentCount > 0 && (
                      <>
                        <MessageSquareText className="mr-1" size={14} />
                        {request.commentCount}
                      </>
                    )}
                  </span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>{formatDistanceToNow(request.createdAt)} ago</div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Created on {format(request.createdAt, 'PPpp')}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <ChangeStatusDropdown
                  currentStatus={request.status}
                  isWithRemoveOption
                  onSelect={(status) => handleChangeStatus(request, status)}
                  onRemove={() => setRequestToRemove(request)}
                >
                  <Button className="h-9 min-w-9 space-x-1 p-2" variant="secondary">
                    {status && (
                      <status.Icon className={`${status.iconClassName} shrink-0`} size={16} />
                    )}
                    <ChevronDown className="text-muted-foreground" size={18} />
                  </Button>
                </ChangeStatusDropdown>
                <ChangePriorityDropdown
                  currentPriority={request.priority}
                  onSelect={(priority) => handleChangePriority(request, priority)}
                >
                  <Button className="h-9 min-w-9 space-x-1 p-2" variant="secondary">
                    {priority && <priority.Icon className="shrink-0" size={16} />}
                    <ChevronDown className="text-muted-foreground" size={18} />
                  </Button>
                </ChangePriorityDropdown>
              </div>
            </div>
          );
        }

        return (
          <div
            key={request.id}
            className="flex min-h-[70px] cursor-pointer items-center gap-3 px-3 py-3 transition-colors hover:bg-slate-100/30 dark:hover:bg-sidebar/50"
            onClick={() => {
              onItemClick(request.id);
            }}
          >
            <RequestVoteCount count={request.voteCount} />
            <div className="flex-1 flex-col">
              <div className="line-clamp-2 font-semibold">{request.name}</div>
              <div className="text-muted-foreground line-clamp-2 text-sm">
                {request.description}
              </div>
              <div className="text-muted-foreground/60 mt-1 flex items-center gap-1.5 space-x-0.5 text-sm">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>{formatDistanceToNow(request.createdAt)} ago</div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Created on {format(request.createdAt, 'PPpp')}</p>
                  </TooltipContent>
                </Tooltip>
                {!subscription?.isFree && request.commentCount > 0 && (
                  <>
                    <span>•</span>
                    <span className="flex items-center">
                      <MessageSquareText className="mr-1" size={14} />
                      {request.commentCount}
                    </span>
                  </>
                )}
                {request.platform && (
                  <>
                    <span>•</span>
                    <span>
                      {request.platform === 'ios' ? (
                        <IosIcon className="text-black dark:text-white" width={16} height={16} />
                      ) : (
                        <AndroidIcon width={18} height={18} />
                      )}
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-start gap-1.5">
              {quickActions?.map((quickAction) => (
                <Tooltip key={quickAction.status}>
                  <TooltipTrigger asChild>
                    <Button
                      className="h-9 w-9 p-0"
                      variant="outline"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleChangeStatus(request, quickAction.status);
                      }}
                    >
                      <quickAction.Icon className={quickAction.iconClassName} size={18} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{quickAction.tooltip}</TooltipContent>
                </Tooltip>
              ))}
              <ChangeStatusDropdown
                currentStatus={request.status}
                isWithRemoveOption
                onSelect={(status) => handleChangeStatus(request, status)}
                onRemove={() => setRequestToRemove(request)}
              >
                <Button className="h-9 min-w-9 space-x-1 p-2" variant="secondary">
                  {status && (
                    <status.Icon className={`${status.iconClassName} shrink-0`} size={16} />
                  )}
                  <ChevronDown className="text-muted-foreground" size={18} />
                </Button>
              </ChangeStatusDropdown>
              <ChangePriorityDropdown
                currentPriority={request.priority}
                onSelect={(priority) => handleChangePriority(request, priority)}
              >
                <Button className="h-9 min-w-9 space-x-1 p-2" variant="secondary">
                  {priority && <priority.Icon className="shrink-0" size={16} />}
                  <ChevronDown className="text-muted-foreground" size={18} />
                </Button>
              </ChangePriorityDropdown>
            </div>
          </div>
        );
      })}
      <ConfirmDeleteAlert
        title="Delete Request"
        description="This action cannot be undone. This will permanently delete this request."
        isOpen={!!requestToRemove}
        onAction={handleDeleteAction}
      />
    </div>
  );
};
